import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/contexts/LocaleContext';
import { optimizedImageUrl } from '@/lib/storage';
import { FavoriteButton } from '@/components/FavoriteButton';
import { AddToCartButton } from '@/components/AddToCartButton';
import { CarouselScroller } from './CarouselScroller';

interface Activity {
  id: string;
  title_ar: string;
  title_en?: string | null;
  price_kwd: number;
  category_id?: string | null;
  gallery_urls?: string[] | null;
}

interface Category {
  id: string;
  name_ar: string;
  name_en?: string | null;
}

interface Props {
  title: string;
  viewAllHref: string;
  activities: Activity[];
  categories: Category[];
  eager?: boolean;
}

export function ActivityCarouselRow({ title, viewAllHref, activities, categories, eager = false }: Props) {
  const { t, locale } = useLocale();
  const isArabic = locale === 'ar';

  if (activities.length === 0) return null;

  const featured = activities.slice(0, 10);

  return (
    <section className="py-6 md:py-8">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h2 className="text-lg md:text-2xl font-bold">{title}</h2>
          <Button variant="ghost" asChild>
            <Link to={viewAllHref}>{t('viewAll')} →</Link>
          </Button>
        </div>

        <CarouselScroller arrowsTop="35%" resetKey={activities.length} className="scroll-ps-4 scroll-pe-4">
          <div className={`grid grid-flow-col auto-cols-[45%] sm:auto-cols-[32%] md:auto-cols-[calc((100%-5rem)/5)] gap-3 md:gap-4 ${featured.length < 5 ? 'md:justify-start' : ''}`}>

            {featured.map((activity, i) => {
              const activityTitle = isArabic ? activity.title_ar : (activity.title_en || activity.title_ar);
              const category = categories.find((c) => c.id === activity.category_id);
              const categoryName = category ? (isArabic ? category.name_ar : (category.name_en || category.name_ar)) : '';
              const imageUrl = activity.gallery_urls?.[0] || 'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=400';

              return (
                <Link
                  key={activity.id}
                  to={`/activities/${activity.id}`}
                  className="animate-fade-in snap-start group cursor-pointer block"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="aspect-[4/5] overflow-hidden relative rounded-2xl bg-muted">
                    <img
                      src={optimizedImageUrl(imageUrl, { width: 500, quality: 70 })}
                      alt={activityTitle}
                      width={500}
                      height={625}
                      loading={eager && i < 2 ? 'eager' : 'lazy'}
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
                    <div className="absolute top-2 end-2 z-10 flex flex-col gap-1.5">
                      <FavoriteButton activityId={activity.id} size="sm" />
                      <AddToCartButton activityId={activity.id} size="sm" />
                    </div>
                    {categoryName && (
                      <span className="absolute top-2 start-2 z-10 inline-block text-[10px] md:text-xs font-medium text-[hsl(15_70%_28%)] bg-white/90 backdrop-blur px-2 py-0.5 rounded-full shadow-sm">
                        {categoryName}
                      </span>
                    )}
                    <div className="absolute bottom-3 start-3 end-3">
                      <h3 className="text-white text-sm md:text-base font-extrabold drop-shadow-md line-clamp-1 leading-tight">{activityTitle}</h3>
                      <p className="text-white/90 text-xs md:text-sm mt-0.5 line-clamp-1 drop-shadow">
                        {activity.price_kwd} {t('kwd')}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </CarouselScroller>
      </div>
    </section>
  );
}

